/**
 * whiteboardReveal — pure builder for the post-import "reveal".
 *
 * After a whiteboard import commits (WhiteboardImportModal → confirmImport /
 * applyMerge), the modal still holds everything the payoff moment needs: the
 * plan (titles, categories, altitudes, board positions), the created item and
 * edge identities, and the capture photo. This module projects that committed
 * state into a RevealScript — the data contract consumed by
 * WhiteboardRevealOverlay for the fog-lift animation, the "play what happened"
 * walkthrough, and the exportable diagram.
 *
 * Deliberately pure and deterministic (in the style of buildImportPlan /
 * mergeCapturedScene) so the narration and node/edge selection are unit-testable.
 * It reads plan/merge outputs only; it can never influence what was parsed,
 * planned, or committed.
 */

import type { WhiteboardImportPlan } from './whiteboardImportService';
import type { MergeResult } from './whiteboardMerge';
import type { WhiteboardBoard } from './whiteboardBaselineStore';
import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';
import type {
  RelationshipEdgeType,
  RelationshipDirectionality,
} from '../types/prioritization/itemRelationships';

// ── Script types ─────────────────────────────────────────────────────────────

/** How a node arrived in the workspace — drives its visual treatment. */
export type RevealNodeKind = 'created' | 'reused' | 'changed';

export interface RevealNode {
  /** The real platform item id this node committed to. */
  itemId: string;
  title: string;
  /** Normalized board position, origin top-left, x/y in [0,1]. */
  position: { x: number; y: number };
  /** Raw board color name (yellow, red, unknown, …) — fallback tint. */
  color: string;
  itemCategory?: ItemLanguageType;
  altitudeLevel?: AltitudeLevel;
  kind: RevealNodeKind;
}

export interface RevealEdge {
  sourceItemId: string;
  targetItemId: string;
  type: RelationshipEdgeType;
  directionality: RelationshipDirectionality;
  label?: string;
}

export type RevealStepId =
  | 'photo'
  | 'recognition'
  | 'items'
  | 'relationships'
  | 'structure'
  | 'home'
  | 'next';

export interface RevealStep {
  id: RevealStepId;
  /** One plain sentence, built from the real counts and names. */
  narration: string;
  /** What this scene emphasizes; everything else dims. */
  highlight?: { nodeIds?: string[]; edgeIdx?: number[] };
}

export interface RevealSummary {
  created: number;
  reused: number;
  /** Present on the re-capture flavor: renamed + moved items. */
  changed?: number;
  relationships: number;
  collection: string | null;
}

export interface RevealScript {
  boardTitle: string;
  /** The capture photo (data URL) — the fog-lift backdrop. Optional. */
  photoUrl?: string;
  nodes: RevealNode[];
  edges: RevealEdge[];
  steps: RevealStep[];
  summary: RevealSummary;
}

// ── Shared helpers ───────────────────────────────────────────────────────────

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/**
 * Reading order for the materialize stagger: coarse top-to-bottom bands, then
 * left-to-right within a band — so nodes appear the way an eye scans the wall.
 */
export function sortNodesReadingOrder(nodes: RevealNode[]): RevealNode[] {
  return [...nodes].sort((a, b) => {
    const bandA = Math.round(clamp01(a.position.y) * 8);
    const bandB = Math.round(clamp01(b.position.y) * 8);
    if (bandA !== bandB) return bandA - bandB;
    return a.position.x - b.position.x;
  });
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

type Flavor = 'import' | 'recapture' | 'replay';

/** Build the walkthrough scenes from the assembled nodes/edges/summary. */
function buildSteps(input: {
  boardTitle: string;
  photoUrl?: string;
  nodes: RevealNode[];
  edges: RevealEdge[];
  summary: RevealSummary;
  flavor: Flavor;
}): RevealStep[] {
  const { boardTitle, photoUrl, nodes, edges, summary, flavor } = input;
  const steps: RevealStep[] = [];

  if (photoUrl) {
    steps.push({
      id: 'photo',
      narration:
        flavor === 'recapture'
          ? `The wall, photographed again — “${boardTitle}”.`
          : `This is the wall — “${boardTitle}”. Marker ink, stickies, arrows.`,
    });
  }

  steps.push({
    id: 'recognition',
    narration: `${plural(nodes.length, 'shape')} and ${plural(edges.length, 'connector')} became structure — nothing was retyped.`,
  });

  const createdIds = nodes.filter((n) => n.kind === 'created').map((n) => n.itemId);
  const changedIds = nodes.filter((n) => n.kind === 'changed').map((n) => n.itemId);
  let itemsNarration: string;
  if (flavor === 'recapture') {
    itemsNarration =
      summary.created + (summary.changed ?? 0) === 0
        ? 'Every box matched the previous version — the same items, still in place.'
        : `${plural(summary.created, 'new item')} and ${plural(summary.changed ?? 0, 'change')} — matched back to the same items as last time.`;
  } else if (summary.created === 0 && summary.reused > 0) {
    itemsNarration =
      summary.reused === 1
        ? 'The box matched an item you already had — nothing was duplicated.'
        : `All ${summary.reused} boxes matched items you already had — nothing was duplicated.`;
  } else {
    itemsNarration = `${plural(summary.created, 'new item')} joined your workspace${
      summary.reused > 0 ? `, and ${summary.reused} matched existing item${summary.reused === 1 ? ' was' : 's were'} reused` : ''
    }.`;
  }
  steps.push({
    id: 'items',
    narration: itemsNarration,
    highlight: { nodeIds: createdIds.length > 0 ? createdIds : changedIds.length > 0 ? changedIds : undefined },
  });

  if (edges.length > 0) {
    steps.push({
      id: 'relationships',
      narration: `${plural(edges.length, 'relationship')} came straight from the arrows and groupings you drew.`,
      highlight: { edgeIdx: edges.map((_, i) => i) },
    });
  }

  steps.push({
    id: 'structure',
    narration: 'Height on the board became altitude — strategic at the top, tactical at the bottom.',
  });

  steps.push({
    id: 'home',
    narration: summary.collection
      ? `It's grouped as “${summary.collection}” — a set you can find again under Items → Whiteboards.`
      : 'The items now live in your library, and the board is saved under Items → Whiteboards.',
  });

  steps.push({
    id: 'next',
    narration:
      flavor === 'replay'
        ? 'From here: re-capture the wall to fold in changes, or put these items into a session.'
        : 'Next: open the board, or put what the wall surfaced into a prioritization session.',
  });

  return steps;
}

// ── Builders ─────────────────────────────────────────────────────────────────

/**
 * Fresh-import flavor (Exit A — confirmImport). Mirrors the commit's own
 * selection rules: included nodes only; 'use_existing' resolutions point at the
 * matched real item; relationships only between nodes that actually landed.
 */
export function buildRevealScript(
  plan: WhiteboardImportPlan,
  opts: { photoUrl?: string; collection: string | null },
): RevealScript {
  const refToItemId = new Map<string, string>();
  const nodes: RevealNode[] = [];

  for (const it of plan.items) {
    if (!it.include) continue;
    const reused = it.resolution === 'use_existing' && !!it.duplicateOf;
    if (!reused && !it.title.trim()) continue;
    const itemId = reused ? it.duplicateOf!.existingItemId : it.itemId;
    refToItemId.set(it.ref, itemId);
    nodes.push({
      itemId,
      title: it.title.trim(),
      position: it.position,
      color: it.color,
      itemCategory: it.itemCategory,
      altitudeLevel: it.altitudeLevel,
      kind: reused ? 'reused' : 'created',
    });
  }

  const edges: RevealEdge[] = [];
  for (const rel of plan.relationships) {
    if (!rel.include) continue;
    const sourceItemId = refToItemId.get(rel.sourceRef);
    const targetItemId = refToItemId.get(rel.targetRef);
    if (!sourceItemId || !targetItemId || sourceItemId === targetItemId) continue;
    edges.push({
      sourceItemId,
      targetItemId,
      type: rel.type,
      directionality: rel.directionality,
      label: rel.label,
    });
  }

  const sorted = sortNodesReadingOrder(nodes);
  const summary: RevealSummary = {
    created: sorted.filter((n) => n.kind === 'created').length,
    reused: sorted.filter((n) => n.kind === 'reused').length,
    relationships: edges.length,
    collection: opts.collection,
  };

  return {
    boardTitle: plan.title?.trim() || opts.collection || 'Whiteboard',
    photoUrl: opts.photoUrl,
    nodes: sorted,
    edges,
    steps: buildSteps({
      boardTitle: plan.title?.trim() || opts.collection || 'Whiteboard',
      photoUrl: opts.photoUrl,
      nodes: sorted,
      edges,
      summary,
      flavor: 'import',
    }),
    summary,
  };
}

/**
 * Re-capture flavor (Exit B — applyMerge). New items materialize, changed items
 * pulse, unchanged items render as the steady background the change sits in.
 */
export function buildRevealScriptFromMerge(
  merge: MergeResult,
  board: { title: string },
  opts: { photoUrl?: string } = {},
): RevealScript {
  const nodes: RevealNode[] = merge.items.map((it) => ({
    itemId: it.itemId,
    title: it.title,
    position: it.position,
    color: it.color ?? 'unknown',
    itemCategory: it.itemCategory,
    altitudeLevel: it.altitudeLevel,
    kind: it.status === 'new' ? 'created' : it.status === 'unchanged' ? 'reused' : 'changed',
  }));

  const known = new Set(nodes.map((n) => n.itemId));
  const edges: RevealEdge[] = merge.relationships
    .filter((r) => known.has(r.sourceItemId) && known.has(r.targetItemId))
    .map((r) => ({
      sourceItemId: r.sourceItemId,
      targetItemId: r.targetItemId,
      type: r.type,
      directionality: r.directionality,
    }));

  const sorted = sortNodesReadingOrder(nodes);
  const summary: RevealSummary = {
    created: merge.summary.added,
    reused: merge.summary.unchanged,
    changed: merge.summary.renamed + merge.summary.moved,
    relationships: merge.summary.relationshipsAdded,
    collection: null,
  };

  return {
    boardTitle: board.title,
    photoUrl: opts.photoUrl,
    nodes: sorted,
    edges,
    steps: buildSteps({
      boardTitle: board.title,
      photoUrl: opts.photoUrl,
      nodes: sorted,
      edges,
      summary,
      flavor: 'recapture',
    }),
    summary,
  };
}

/**
 * Durable replay flavor — rebuild the reveal later from a saved board
 * (whiteboardBaselineStore) plus live item lookups, the same way
 * WhiteboardBoardsPage reads current titles/attributes through the baseline.
 */
export function buildRevealScriptFromBaseline(
  board: WhiteboardBoard,
  live: {
    categoryOf: (itemId: string) => ItemLanguageType | undefined;
    altitudeOf: (itemId: string) => AltitudeLevel | undefined;
  },
  opts: { photoUrl?: string } = {},
): RevealScript {
  const nodes: RevealNode[] = board.baseline.items.map((it) => ({
    itemId: it.id,
    title: it.title,
    position: it.position,
    color: 'unknown',
    itemCategory: live.categoryOf(it.id),
    altitudeLevel: live.altitudeOf(it.id),
    kind: 'created',
  }));

  const known = new Set(nodes.map((n) => n.itemId));
  const edges: RevealEdge[] = board.baseline.relationships
    .filter((r) => known.has(r.sourceItemId) && known.has(r.targetItemId))
    .map((r) => ({
      sourceItemId: r.sourceItemId,
      targetItemId: r.targetItemId,
      type: r.type,
      directionality: r.directionality,
    }));

  const sorted = sortNodesReadingOrder(nodes);
  const summary: RevealSummary = {
    created: sorted.length,
    reused: 0,
    relationships: edges.length,
    collection: board.title,
  };

  return {
    boardTitle: board.title,
    photoUrl: opts.photoUrl,
    nodes: sorted,
    edges,
    steps: buildSteps({
      boardTitle: board.title,
      photoUrl: opts.photoUrl,
      nodes: sorted,
      edges,
      summary,
      flavor: 'replay',
    }),
    summary,
  };
}
